import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardCheck,
  GraduationCap,
  Rocket,
  CheckCircle2,
  FileText,
  Sparkles,
  CalendarCheck,
} from "lucide-react";

const CALENDLY = "https://calendly.com/wordgumbolearningco";

const PROGRAMS = [
  {
    key: "testing",
    title: "Comprehensive Dyslexia Testing",
    icon: ClipboardCheck,
    accent: "bg-wg-purple",
    chip: "bg-wg-purple/10 text-wg-purple",
    intro:
      "Our evaluations are designed to identify characteristics of dyslexia and provide a deeper understanding of your child's reading strengths and challenges.",
    blocks: [
      {
        heading: "Assessments may include",
        icon: Sparkles,
        items: [
          "Woodcock-Johnson Achievement Assessment",
          "TOD-C (Test of Dyslexia – Comprehensive)",
          "Reading fluency and comprehension measures",
          "Phonological awareness and spelling assessments",
        ],
      },
      {
        heading: "Families receive",
        icon: FileText,
        items: [
          "A comprehensive written report",
          "Clear recommendations for support and intervention",
          "Documentation for school meetings and accommodations",
        ],
      },
    ],
  },
  {
    key: "therapy",
    title: "Dyslexia Therapy & Reading Intervention",
    icon: GraduationCap,
    accent: "bg-wg-teal",
    chip: "bg-wg-teal/10 text-wg-teal",
    intro:
      "We use a structured literacy approach grounded in the Orton-Gillingham methodology to support struggling readers and students with dyslexia. Instruction is explicit, systematic, multisensory, and individualized — building confidence and long-term reading success.",
    blocks: [
      {
        heading: "Instruction may include",
        icon: Sparkles,
        items: [
          "Explicit phonics instruction",
          "Phonological awareness training",
          "Multisensory learning strategies",
          "Fluency and comprehension support",
          "Spelling and writing support",
        ],
      },
      {
        heading: "Curriculum & approaches",
        icon: FileText,
        items: [
          "Orton-Gillingham-based instruction",
          "Structured literacy practices",
          "Decodable texts",
          "Research-based reading intervention resources",
        ],
      },
    ],
  },
  {
    key: "take-flight",
    title: "Take Flight Intervention",
    icon: Rocket,
    accent: "bg-wg-orange",
    chip: "bg-wg-orange/10 text-wg-orange",
    intro:
      "We utilize the Take Flight curriculum, a comprehensive dyslexia intervention program developed by the Luke Waites Center for Dyslexia and Learning Disorders at Texas Scottish Rite Hospital for Children — a highly structured, research-based program designed specifically for students with dyslexia.",
    blocks: [
      {
        heading: "The program targets",
        icon: Sparkles,
        items: [
          "Phonemic awareness",
          "Decoding and encoding skills",
          "Reading fluency",
          "Reading comprehension",
          "Written expression",
        ],
      },
    ],
    footer:
      "Our goal is to provide engaging, evidence-based instruction that helps students become confident, capable readers while fostering a love for learning.",
  },
];

export default function Programs() {
  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 border border-wg-purple/15">
            <span className="w-1.5 h-1.5 rounded-full bg-wg-purple" />
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-wg-purple">Programs & Assessments</span>
          </div>
          <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-wg-ink leading-tight">
            Programs & assessments <span className="wg-text-grad">we use.</span>
          </h2>
          <p className="mt-5 text-lg text-wg-ink2 max-w-2xl">
            Evidence-based methodologies and trusted assessment tools — chosen specifically to give your child
            the structured, multisensory support that produces real reading gains.
          </p>
        </motion.div>

        <div
          data-testid="programs-grid"
          className="mt-12 grid gap-7 lg:grid-cols-3"
        >
          {PROGRAMS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`program-card-${p.key}`}
                className="group bg-white rounded-[1.8rem] p-8 md:p-9 border border-wg-purple/10 shadow-[0_10px_30px_rgb(74,29,82,0.05)] hover:shadow-[0_22px_50px_rgb(74,29,82,0.10)] hover:-translate-y-1.5 transition-all duration-300 flex flex-col"
              >
                <motion.div
                  whileHover={{ rotate: [0, -8, 8, 0], scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl ${p.accent} text-white shadow-md`}
                >
                  <Icon className="w-7 h-7" />
                </motion.div>

                <h3 className="mt-6 font-heading font-bold text-2xl leading-tight text-wg-ink">
                  {p.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-wg-ink2">
                  {p.intro}
                </p>

                <div className="mt-6 space-y-5">
                  {p.blocks.map((b) => {
                    const BIcon = b.icon;
                    return (
                      <div key={b.heading}>
                        <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${p.chip}`}>
                          <BIcon className="w-3.5 h-3.5" />
                          {b.heading}
                        </div>
                        <ul className="mt-3 space-y-2">
                          {b.items.map((it) => (
                            <li key={it} className="flex items-start gap-2 text-[14.5px] text-wg-ink2 leading-relaxed">
                              <CheckCircle2 className="w-4 h-4 mt-0.5 text-wg-green shrink-0" />
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                {p.footer && (
                  <p className="mt-6 pt-5 border-t border-wg-purple/10 text-[14.5px] text-wg-ink2 leading-relaxed italic">
                    {p.footer}
                  </p>
                )}
              </motion.article>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <a
            href={CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="programs-cta-btn"
            className="inline-flex items-center gap-2 bg-wg-purple text-white rounded-full px-7 py-4 font-semibold hover:bg-wg-purpleDeep hover:-translate-y-0.5 shadow-lg shadow-wg-purple/30 transition-all"
          >
            <CalendarCheck className="w-5 h-5" />
            Talk Through the Right Program for Your Child
          </a>
        </div>
      </div>
    </section>
  );
}
